import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Key, Copy, Plus, Trash2, Check, ShieldCheck, Terminal, BookOpen, Clock, Globe, Cpu, Shield, LogIn, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage.js';
import { BrandingSettingsSection } from '@/components/settings/BrandingSettingsSection.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { Skeleton } from '@/components/ui/skeleton.js';

interface ApiKeyItem {
  id: string;
  name: string;
  key_prefix: string;
  masked_key: string;
  raw_key?: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string | null;
}

export function SettingsPage() {
  const { user } = useAuth();
  if (user && user.role !== 'owner') {
    return (
      <UnauthorizedPage
        requiredRoles={['owner']}
        message="Settings and Scoped API Keys are strictly restricted to agency owners."
      />
    );
  }

  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    'projects:read',
    'time:read',
    'team:read',
  ]);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState<string | null>(null);

  // Delete / Revoke Confirmation State
  const [revokeKeyTarget, setRevokeKeyTarget] = useState<ApiKeyItem | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!showCreateModal && !revokeKeyTarget) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCreateModal(false);
        setRevokeKeyTarget(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCreateModal, revokeKeyTarget]);

  const availableScopes = [
    { id: 'projects:read', label: 'Read Projects & Derived Progress' },
    { id: 'projects:write', label: 'Create & Update Projects' },
    { id: 'tasks:write', label: 'Create & Update Tasks' },
    { id: 'time:read', label: 'Read Employee Time Entries' },
    { id: 'team:read', label: 'Read Team Directory & Workload' },
    { id: 'reports:read', label: 'Read Monthly Historical Reports' },
    { id: 'finances:read', label: 'Read Financial Position (Owner Only)' },
  ];

  const fetchKeys = async () => {
    try {
      const res = await api.get<{ data: ApiKeyItem[] }>('/api/v1/api-keys');
      if (res?.data && Array.isArray(res.data)) {
        setApiKeys(res.data);
      } else if (Array.isArray(res)) {
        setApiKeys(res);
      }
    } catch (e) {
      console.error('Failed to fetch API keys:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleToggleScope = (scopeId: string) => {
    if (selectedScopes.includes(scopeId)) {
      setSelectedScopes(selectedScopes.filter((s) => s !== scopeId));
    } else {
      setSelectedScopes([...selectedScopes, scopeId]);
    }
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyName.trim()) return;
    try {
      const res = await api.post('/api/v1/api-keys', {
        name: keyName.trim(),
        scopes: selectedScopes,
      });
      const created = res?.data || res;
      if (created?.raw_key) {
        setGeneratedKey(created.raw_key);
      }
      setKeyName('');
      fetchKeys();
    } catch (e: any) {
      alert(e.message || 'Failed to generate API key');
    }
  };

  const confirmRevokeKey = async () => {
    if (!revokeKeyTarget) return;
    try {
      await api.delete(`/api/v1/api-keys/${revokeKeyTarget.id}`);
      setRevokeKeyTarget(null);
      fetchKeys();
    } catch (e: any) {
      alert(e.message || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    if (id === 'generated') {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } else {
      setCopiedCurl(id);
      setTimeout(() => setCopiedCurl(null), 2000);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Page Header */}
      <div>
        <h1 className="page-title">Settings & Developer API</h1>
        <p className="helper-text mt-1">Manage scoped API keys, REST API access, and workspace defaults.</p>
      </div>

      {/* Workspace Configuration Overview */}
      <Card>
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="text-base font-semibold text-brand-navy flex items-center space-x-2">
            <Globe className="h-4 w-4 text-brand-teal" />
            <span>Workspace Preferences</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Global agency defaults applied to time calculations and reporting.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Agency Organization</div>
            <div className="text-sm font-bold text-brand-navy mt-1">Techosolution Agency</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Software & WordPress Engineering</div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center space-x-1">
              <Clock className="h-3.5 w-3.5 text-brand-teal" />
              <span>Accounting Timezone</span>
            </div>
            <div className="text-sm font-bold text-brand-navy mt-1">Asia/Karachi (PKT, UTC+5)</div>
            <div className="text-[11px] text-emerald-700 font-medium mt-0.5">Boundary splitting enforced</div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Weekly Capacity Schedule</div>
            <div className="text-sm font-bold text-brand-navy mt-1">40 Hours / Employee</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Monday – Friday standard schedule</div>
          </div>
        </CardContent>
      </Card>

      {/* Workspace Authentication & Login Security Section */}
      <Card className="border-brand-border">
        <CardHeader className="border-b border-slate-100 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold text-brand-navy flex items-center space-x-2">
              <Shield className="h-4 w-4 text-brand-teal" />
              <span>Authentication &amp; Member Login Security</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Continuous 2FA verification for workspace owners and real-time login activity audit stream across all members.
            </CardDescription>
          </div>
          <Link to="/activity?tab=login_activity">
            <Button size="sm" variant="outline" className="flex items-center space-x-1.5 text-xs text-brand-teal border-brand-teal/40 hover:bg-brand-tint/20 shrink-0">
              <LogIn className="h-3.5 w-3.5" />
              <span>View Login Activity</span>
              <ArrowRight className="h-3 w-3 ml-0.5" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-lg border border-emerald-200 dark:border-emerald-900/50">
            <div className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-400 uppercase tracking-wide flex items-center space-x-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>Owner 2FA Email OTP</span>
            </div>
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-1">Active &amp; Enforced</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Dispatches 6-digit verification code to email on every owner sign-in</div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center space-x-1">
              <Clock className="h-3.5 w-3.5 text-brand-teal" />
              <span>Session Duration &amp; Lockout</span>
            </div>
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-1">5 Attempts &amp; 60s Lockout</div>
            <div className="text-[11px] text-slate-500 mt-0.5">5-minute OTP code validity with max 5 verification attempts</div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center space-x-1">
              <Globe className="h-3.5 w-3.5 text-brand-teal" />
              <span>Member Device Tracking</span>
            </div>
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-1">IP &amp; Device Auditing</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Surfaced on Team Directory and Member Login Activity audit tab</div>
          </div>
        </CardContent>
      </Card>

      {/* Agency Branding & Visual Assets Section */}
      <BrandingSettingsSection />

      {/* MCP Tool Configuration Banner */}
      <Card className="border-brand-border bg-gradient-to-r from-surface to-brand-tint/30">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] text-white flex items-center justify-center shrink-0 shadow-sm">
              <Cpu className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-brand-ink">MCP Tool Configuration & Safety Controls</h3>
              <p className="text-xs text-brand-muted mt-0.5">
                Manage 23+ MCP tools, apply safety presets (Read-Only, Disable Destructive), and configure agent permissions.
              </p>
            </div>
          </div>
          <Link to="/mcp-settings">
            <Button size="sm" className="bg-[#087F75] hover:bg-[#06685F] text-white font-semibold text-xs shrink-0">
              Open MCP Settings
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Scoped API Keys Management */}
      <Card>
        <CardHeader className="border-b border-slate-100 pb-4 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold text-brand-navy flex items-center space-x-2">
              <Key className="h-4 w-4 text-brand-teal" />
              <span>Scoped API Keys</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Keys provide programmatic access for AI agents (Claude, Cursor) and automation tools. Live timer starts are restricted to human browser sessions.
            </CardDescription>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand-teal hover:bg-teal-700 text-white flex items-center space-x-1 text-xs"
          >
            <Plus className="h-4 w-4" />
            <span>Generate New Key</span>
          </Button>
        </CardHeader>

        <CardContent className="pt-4">
          {loading ? (
            <div className="py-4 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1.5 flex-1">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-4 w-44" />
                      <Skeleton className="h-5 w-32 rounded" />
                    </div>
                    <Skeleton className="h-3 w-56" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-7 w-7 rounded-md" />
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">No API keys created yet. Generate one to enable agent integration.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {apiKeys.map((k) => (
                <div key={k.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-sm text-brand-navy">{k.name}</span>
                      <code className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-700 border border-slate-200">
                        {k.masked_key}
                      </code>
                    </div>
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {k.scopes.map((s) => (
                        <Badge key={s} variant="secondary" className="text-[10px] py-0 px-1.5 font-normal">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-slate-500">
                    <span>Created: {new Date(k.created_at).toLocaleDateString()}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRevokeKeyTarget(k)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      <span>Revoke</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* REST API & Model Context Protocol Reference */}
      <Card>
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="text-base font-semibold text-brand-navy flex items-center space-x-2">
            <BookOpen className="h-4 w-4 text-brand-teal" />
            <span>REST API & Model Context Protocol (MCP) Reference</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Authenticate requests using standard HTTP Bearer token authorization: <code className="bg-slate-100 px-1 rounded">Authorization: Bearer tsos_live_...</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="space-y-3">
            {[
              {
                method: 'GET',
                route: '/api/v1/projects',
                desc: 'List active client projects with derived progress % and recorded hours.',
                curl: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://blue-rail-666337.hostingersite.com/api/v1/projects',
              },
              {
                method: 'GET',
                route: '/api/v1/tasks',
                desc: 'Query tasks with assignee, priority, status, and checklist details.',
                curl: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://blue-rail-666337.hostingersite.com/api/v1/tasks',
              },
              {
                method: 'GET',
                route: '/api/v1/reports/team?month=2026-10',
                desc: 'Karachi-month bounded employee historical audit report.',
                curl: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://blue-rail-666337.hostingersite.com/api/v1/reports/team?month=2026-10',
              },
            ].map((ep, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-xs font-bold bg-brand-navy text-white px-2 py-0.5 rounded">
                      {ep.method}
                    </span>
                    <span className="font-mono text-xs font-semibold text-slate-800">{ep.route}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(ep.curl, `curl_${idx}`)}
                    className="h-7 text-xs flex items-center space-x-1 text-slate-600 hover:text-brand-teal"
                  >
                    {copiedCurl === `curl_${idx}` ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                    <span>{copiedCurl === `curl_${idx}` ? 'Copied' : 'Copy cURL'}</span>
                  </Button>
                </div>
                <p className="text-xs text-slate-600">{ep.desc}</p>
                <code className="block bg-slate-900 text-slate-200 text-[11px] font-mono p-2 rounded overflow-x-auto">
                  {ep.curl}
                </code>
              </div>
            ))}
          </div>

          {/* MCP Stdio Setup Guide */}
          <div className="mt-4 p-4 rounded-lg bg-teal-50/70 border border-teal-200">
            <div className="flex items-center space-x-2 text-teal-900 font-semibold text-xs mb-1">
              <Terminal className="h-4 w-4 text-brand-teal" />
              <span>Connect MCP Server (Cursor & Claude Desktop)</span>
            </div>
            <p className="text-xs text-teal-800">
              Add Techosolution Project OS tools directly to your local AI coding assistant by specifying your API key in <code className="bg-teal-100/80 px-1 py-0.5 rounded">claude_desktop_config.json</code>:
            </p>
            <pre className="mt-2 text-[11px] font-mono bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
{`"techosolution-project-os": {
  "command": "node",
  "args": ["d:/OneDrive/fiverr/projects/techosolution-project-os/mcp-server/dist/index.js"],
  "env": {
    "PROJECT_OS_API_URL": "https://blue-rail-666337.hostingersite.com/api",
    "PROJECT_OS_API_KEY": "YOUR_API_KEY"
  }
}`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Generate API Key Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-brand-navy flex items-center space-x-2">
              <ShieldCheck className="h-5 w-5 text-brand-teal" />
              <span>Generate Scoped API Key</span>
            </h2>

            {generatedKey ? (
              <div className="space-y-4 py-2">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-xs">
                  <span className="font-bold block mb-1">⚠️ Important: Save your key now!</span>
                  This secret key will never be shown again. Store it securely in your password manager or environment configuration.
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">API Secret Key</label>
                  <div className="flex items-center space-x-2">
                    <Input readOnly value={generatedKey} className="font-mono text-xs bg-slate-50" />
                    <Button
                      onClick={() => copyToClipboard(generatedKey, 'generated')}
                      className="bg-brand-teal hover:bg-teal-700 text-white shrink-0"
                    >
                      {copiedKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => {
                      setGeneratedKey(null);
                      setShowCreateModal(false);
                    }}
                    className="bg-brand-navy text-white"
                  >
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateKey} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Key Name *</label>
                  <Input
                    required
                    placeholder="e.g. Claude Desktop Integration"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Select Authorized Scopes</label>
                  <div className="space-y-1.5 border border-slate-200 p-3 rounded-md bg-slate-50/50 max-h-48 overflow-y-auto">
                    {availableScopes.map((scope) => (
                      <label key={scope.id} className="flex items-start space-x-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.id)}
                          onChange={() => handleToggleScope(scope.id)}
                          className="mt-0.5 accent-brand-teal h-3.5 w-3.5 rounded"
                        />
                        <div>
                          <span className="font-mono font-semibold">{scope.id}</span>
                          <span className="text-slate-500 block text-[11px]">{scope.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                  <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-brand-teal hover:bg-teal-700 text-white">
                    Generate Key
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Shared Revoke / Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!revokeKeyTarget}
        title="Revoke API Key?"
        itemType="API Key"
        itemName={revokeKeyTarget?.name}
        description={`Are you sure you want to permanently revoke API key "${revokeKeyTarget?.name}"? Any active integrations using this key will immediately lose access.`}
        confirmLabel="Revoke Key"
        onClose={() => setRevokeKeyTarget(null)}
        onConfirm={confirmRevokeKey}
      />
    </div>
  );
}
